import type { ExperimentalQueries } from '../__types__/ExperimentalQueries';
import { MediaType } from '../MediaType';
import { throwErrorWithResponse } from '../throwErrorWithResponse';

export const experimentalQuickReload: ExperimentalQueries['experimentalQuickReload'] = (
	_,
	{ contentId, since },
	{ network: { cfetch } },
) => {
	const timeAtFetch = Date.now();
	return cfetch(`/wiki/rest/quickreload/latest/${contentId}/?since=${since}&source=next2`, {
		method: 'GET',
		headers: {
			'Content-Type': MediaType.JSON,
		},
	}).then((res) => {
		if (res.status === 204) {
			/**
			 * The server's saying there are no updates, but doesn't tell us its
			 * timestamp. So we know the 'from' but not the 'to' for its range.
			 * However, the 'to' can't be more recent than the time right before we
			 * issued the fetch, so let's use that. It guarantees at least that we
			 * won't have coverage gaps as we poll.
			 */
			return {
				contentId,
				time: timeAtFetch,
				comments: [],
				editorForPage: null,
			};
		}

		if (res.status === 200) {
			return res.json().then(
				(obj) => ({
					contentId,
					time: obj.time,
					comments: (obj.comments || []).map(({ comment, commenter }: any) => ({
						comment: {
							id: comment.id,
							author: {
								displayName: commenter.displayName,
								accountId: commenter.accountId,
								profilePicture: commenter.profilePicture.path,
							},
							location: comment.isInlineComment ? 'INLINE' : 'FOOTER',
							url: comment.secondaryActions?.[0]?.url,
						},
					})),
					editorForPage: obj.page
						? {
								displayName: obj.page.editor.displayName,
								accountId: obj.page.editor.accountId,
								profilePicture: obj.page.editor.profilePicture.path,
							}
						: null,
				}),
				(error) => throwErrorWithResponse(error, res),
			);
		}

		/**
		 * Classic code would have had a 401 return code trigger a login handler.
		 * We'll just regard any other status code as an error, will throw, and
		 * hence cause the query to return an error. That way, we'll ensure that
		 * the quick reload poller doesn't advance its 'since' time in its
		 * subsequent query.
		 */
		throwErrorWithResponse(Error(`bad status: ${res.status}`), res);
	});
};
